/* eslint-disable max-lines, no-unused-vars */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Play, Loader2, RotateCcw, AlertCircle, CheckCircle2, Cog,
    ChevronRight, Search, Wrench
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import {
    mcpDiscoveryService
} from 'shared/api/services/BEX/mcpDiscovery.service';
import TypedField, {
    coerceTypedFieldValue
} from './TypedField';

/**
 * McpTestPanel — discover + invoke live test for an MCP connection.
 *
 * Two-phase flow per A-0008.07:
 *   1. Discover — POST projecto/config/api/v1/mcp/discover with the saved
 *      connection_id. Returns { tools: [{ name, description, inputSchema }] }.
 *   2. Invoke   — POST projecto/config/api/v1/mcp/invoke with
 *      { connection_id, tool_name, arguments }. Returns { result }.
 *
 * Schema-driven (UF-13): the chosen tool's `inputSchema.properties` drive
 * the arguments form (TypedField per property — strings/numbers/booleans/
 * objects/arrays/enums). `inputSchema.required[]` flips the required flag
 * per field; constraints (pattern, min/max, enum, len) flow through to
 * TypedField hint text.
 *
 * Layout matches OpcodeTestPanel + InstructionTestPanel + ApiTestPanel:
 *   - Default (narrow): inputs on top, result below.
 *   - Maximized: inputs left (440px), result right (flex-1). 2-pane.
 *
 * Requires the MCP connection to be SAVED (connection_id present).
 * Shows a clear "save first" notice otherwise.
 */
export default function McpTestPanel({
    mcp, maximized = false
}) {
    const {
        oragID, subOragID
    } = useParams();
    const connectionId = mcp?.connection_id || mcp?._id || '';
    const [tools, setTools] = useState([]);
    const [discovering, setDiscovering] = useState(false);
    const [discoverError, setDiscoverError] = useState(null);
    const [discoverTimestamp, setDiscoverTimestamp] = useState(null);
    const [toolName, setToolName] = useState('');
    const [argVals, setArgVals] = useState({});
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const selectedTool = useMemo(
        () => tools.find((t) => t.name === toolName) || null,
        [tools, toolName]
    );

    // Inputs schema → field declarations
    const argDecls = useMemo(() => {
        if (!selectedTool?.inputSchema) return [];
        const schema = selectedTool.inputSchema;
        const props = schema.properties || {};
        const required = new Set(Array.isArray(schema.required) ? schema.required : []);
        return Object.entries(props).map(([name, propRaw]) => {
            const prop = propRaw || {};
            const c = {};
            if (prop.pattern) c.pattern = prop.pattern;
            if (prop.minLength != null) c.minLength = prop.minLength;
            if (prop.maxLength != null) c.maxLength = prop.maxLength;
            if (prop.minimum != null) c.minimum = prop.minimum;
            if (prop.maximum != null) c.maximum = prop.maximum;
            if (Array.isArray(prop.enum)) c.enum = prop.enum;
            return {
                name,
                type: prop.type || 'string',
                required: required.has(name),
                description: prop.description,
                constraint: Object.keys(c).length ? c : null,
                default: prop.default
            };
        });
    }, [selectedTool]);

    // Reseed args when tool changes
    useEffect(() => {
        const next = {};
        for (const f of argDecls) {
            next[f.name] = f.default !== undefined ? String(f.default) : '';
        }
        setArgVals(next);
    }, [argDecls]);

    // Auto-discover on mount when we already have a connection_id (cheap,
    // server caches for 5min per A-0008.07). Resets when connection_id changes.
    // We intentionally don't depend on `discover` itself — it's stable per
    // connectionId and including it would cause an infinite refetch loop
    // (it sets state which would re-create the closure).
    useEffect(() => {
        if (!connectionId || !oragID || !subOragID) return;
        discover();
    }, [connectionId, oragID, subOragID]); // discover intentionally omitted

    async function discover() {
        if (!connectionId) {
            setDiscoverError('Save the connection first — discover needs a saved connection_id.');
            return;
        }
        setDiscoverError(null);
        setDiscovering(true);
        try {
            const res = await mcpDiscoveryService.discover({
                org_id: oragID,
                sub_org_id: subOragID,
                connection_id: connectionId
            });
            const toolList = Array.isArray(res?.tools) ? res.tools : [];
            setTools(toolList);
            setDiscoverTimestamp(Date.now());
            // If no tool selected yet, auto-pick the first one.
            if (!toolName && toolList.length > 0) setToolName(toolList[0].name);
        } catch (err) {
            setDiscoverError(mcpErrorMessage(err, 'Discovery failed.'));
            setTools([]);
        } finally {
            setDiscovering(false);
        }
    }

    function clearAll() {
        setArgVals(Object.fromEntries(argDecls.map((f) => [f.name, ''])));
        setResult(null);
        setError(null);
    }

    async function run() {
        if (running) return;
        if (!toolName) {
            setError('Pick a tool first — discover the MCP server, then choose one.');
            return;
        }
        if (!connectionId) {
            setError('Save the MCP connection first — invoke needs a saved connection_id.');
            return;
        }
        setError(null);
        setRunning(true);
        setResult(null);
        const args = {};
        for (const f of argDecls) {
            const raw = argVals[f.name];
            if (raw === '' || raw == null) continue;
            args[f.name] = coerceTypedFieldValue(raw, f.type);
        }
        const startedAt = performance.now();
        try {
            const res = await mcpDiscoveryService.invoke({
                org_id: oragID,
                sub_org_id: subOragID,
                connection_id: connectionId,
                tool_name: toolName,
                arguments: args
            });
            setResult({
                ok: true,
                result: res?.result,
                raw: res,
                request: {
                    tool_name: toolName,
                    arguments: args
                },
                elapsed: Math.round(performance.now() - startedAt)
            });
        } catch (err) {
            setResult({
                ok: false,
                error: mcpErrorMessage(err, String(err)),
                raw: err?.response?.data,
                request: {
                    tool_name: toolName,
                    arguments: args
                },
                elapsed: Math.round(performance.now() - startedAt)
            });
        } finally {
            setRunning(false);
        }
    }

    const argSetCount = Object.values(argVals).filter((v) => v !== '' && v != null).length;

    return (
        <div className={cn(
            'flex h-full overflow-hidden bg-card/30',
            maximized ? 'flex-col lg:flex-row' : 'flex-col'
        )}>
            {/* ── INPUTS PANE ───────────────────────────────────── */}
            <div className={cn(
                'flex flex-col min-h-0',
                maximized ? 'lg:w-[440px] lg:flex-shrink-0 lg:border-r' : 'border-b max-h-[60%]'
            )}>
                {/* MCP summary header */}
                <div className="flex-shrink-0 border-b bg-card px-3 py-2.5">
                    <div className="rounded-md border bg-muted/30 px-2.5 py-1.5">
                        <div className="flex items-start gap-2">
                            <Cog className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-orange-500" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-[12.5px] font-semibold text-foreground">
                                    {mcp?.connection_name || '(unsaved)'}
                                </div>
                                <div className="truncate font-mono text-[10.5px] text-muted-foreground" title={mcp?.server_url}>
                                    {mcp?.server_url || '(no server URL)'}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                    <span className="rounded-full border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                        {(mcp?.transport || 'streamable_http').replace(/_/g, ' ')}
                                    </span>
                                    <span className="rounded-full border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                        auth: {mcp?.auth?.type || 'none'}
                                    </span>
                                    {mcp?.timeout_ms && (
                                        <span className="rounded-full border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                            {mcp.timeout_ms}ms
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
                    {/* Discovery — Re-discover sits inline as a primary action,
                        same affordance shape as Run. */}
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                            <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Discovery</h4>
                            {connectionId ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={discover}
                                    disabled={discovering}
                                    className="h-7 px-2.5"
                                >
                                    {discovering
                                        ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                        : <Search className="mr-1 h-3 w-3" />}
                                    {discovering
                                        ? 'Discovering…'
                                        : tools.length > 0 ? 'Re-discover' : 'Discover'}
                                </Button>
                            ) : (
                                <span className="text-[10px] text-muted-foreground">save first</span>
                            )}
                        </div>
                        {!connectionId && (
                            <div className="rounded-md border border-dashed bg-muted/20 px-2.5 py-1.5 text-[11.5px] text-muted-foreground">
                                Save the connection first — discover needs a connection_id.
                            </div>
                        )}
                        {connectionId && tools.length > 0 && !discovering && (
                            <div className="text-[10.5px] text-muted-foreground">
                                {tools.length} tool{tools.length === 1 ? '' : 's'} cached · server caches for 5 min
                            </div>
                        )}
                        {discoverError && (
                            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
                                <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                <span>{discoverError}</span>
                            </div>
                        )}
                    </div>

                    {/* Tool picker */}
                    {tools.length > 0 && (
                        <Section title="Tool" hint="pick one to invoke">
                            <Select value={toolName} onValueChange={setToolName}>
                                <SelectTrigger className="h-8 text-[12.5px]">
                                    <SelectValue placeholder="Pick a tool" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tools.map((t) => (
                                        <SelectItem key={t.name} value={t.name}>
                                            <span className="font-mono text-[12px]">{t.name}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedTool?.description && (
                                <p className="text-[11px] text-muted-foreground">{selectedTool.description}</p>
                            )}
                        </Section>
                    )}

                    {/* Arguments */}
                    {selectedTool && (
                        <Section
                            title="Arguments"
                            hint={argDecls.length === 0
                                ? 'no args declared'
                                : `${argSetCount}/${argDecls.length}`}
                        >
                            {argDecls.length === 0 ? (
                                <div className="rounded-md border border-dashed bg-muted/20 px-2.5 py-1.5 text-[11.5px] text-muted-foreground">
                                    This tool takes no arguments. Click <b>Run</b> to invoke it.
                                </div>
                            ) : (
                                <div className="space-y-2.5">
                                    {argDecls.map((f) => (
                                        <TypedField
                                            key={f.name}
                                            name={f.name}
                                            type={f.type}
                                            required={f.required}
                                            description={f.description}
                                            constraint={f.constraint}
                                            value={argVals[f.name] ?? ''}
                                            onChange={(v) => setArgVals((p) => ({
                                                ...p,
                                                [f.name]: v
                                            }))}
                                        />
                                    ))}
                                </div>
                            )}
                        </Section>
                    )}

                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[12px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex-shrink-0 border-t bg-background px-3 py-2 flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        disabled={running || (argSetCount === 0 && !result)}
                    >
                        <RotateCcw className="mr-1 h-3 w-3" /> Clear
                    </Button>
                    <Button
                        size="sm"
                        onClick={run}
                        disabled={running || !toolName || !connectionId}
                    >
                        {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
                        {running ? 'Running' : 'Run'}
                    </Button>
                </div>
            </div>

            {/* ── RESULT PANE ───────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-muted/10">
                <ResultBlock
                    running={running}
                    result={result}
                    discovering={discovering}
                    tools={tools}
                />
            </div>
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────

function Section({
    title, hint, children
}) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{title}</h4>
                {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function ResultBlock({
    running, result, discovering, tools
}) {
    if (running) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm italic text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> invoking tool…
            </div>
        );
    }
    if (!result) {
        // Show discovered tools list as orientation, when present
        if (discovering) {
            return (
                <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm italic text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> discovering MCP tools…
                </div>
            );
        }
        if (tools.length > 0) {
            return (
                <div className="space-y-2">
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                        <Wrench className="h-3 w-3" /> Available tools ({tools.length})
                    </div>
                    {tools.map((t) => (
                        <div key={t.name} className="rounded-md border bg-card px-3 py-2">
                            <div className="text-[12.5px] font-mono font-semibold">{t.name}</div>
                            {t.description && (
                                <div className="mt-0.5 text-[11.5px] text-muted-foreground">{t.description}</div>
                            )}
                            {t.inputSchema?.properties && (
                                <div className="mt-1 font-mono text-[10.5px] text-muted-foreground">
                                    args: {Object.keys(t.inputSchema.properties).join(', ') || '(none)'}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className="text-sm text-muted-foreground">
                Click <b>Discover tools</b>, pick one, fill its arguments, and click <b>Run</b>.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className={cn(
                'rounded-md border px-3 py-2 flex items-start gap-2',
                result.ok
                    ? 'border-emerald-300/50 bg-emerald-50/40'
                    : 'border-destructive/40 bg-destructive/5'
            )}>
                {result.ok
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                    : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                }
                <div className="min-w-0 flex-1">
                    <div className={cn(
                        'text-[12.5px] font-semibold',
                        result.ok ? 'text-emerald-800' : 'text-destructive'
                    )}>
                        {result.ok ? 'Completed' : 'Failed'}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                        {result.request?.tool_name}
                        {result.elapsed != null ? ` · ${result.elapsed}ms` : ''}
                    </div>
                    {!result.ok && result.error && (
                        <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] text-destructive">
                            {result.error}
                        </pre>
                    )}
                </div>
            </div>

            {result.ok && (
                <div className="rounded-md border bg-card px-3 py-2.5">
                    <div className="mb-1 flex items-center gap-1.5">
                        <Wrench className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">tool result</span>
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-[12.5px] leading-snug text-foreground max-h-[40vh] overflow-y-auto">
                        {safeStringify(result.result)}
                    </pre>
                </div>
            )}

            <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                    <ChevronRight className="h-3 w-3" />
                    Request sent
                </summary>
                <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                    {safeStringify(result.request)}
                </pre>
            </details>

            {result.raw && (
                <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                    <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                        <ChevronRight className="h-3 w-3" />
                        Raw response
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                        {safeStringify(result.raw)}
                    </pre>
                </details>
            )}
        </div>
    );
}

/**
 * The message the SERVER actually sent, not the one axios made up.
 *
 * Mind reports every MCP failure it can name in FastAPI's `detail` field --
 * `MCP_CONNECT_FAILED: ... Session terminated`, `MCP_CREDENTIAL_REF_INVALID:
 * bearer_token_ref ... missing in Vault`. This panel used to look for `message`,
 * which Mind never sets, so every one of those fell through to axios's
 * "Request failed with status code 400" and the real cause never reached the
 * screen. A wrong address and a stale token looked identical, and both looked
 * like nothing.
 *
 * When there IS no detail (an unhandled 500 answers the bare string "Internal
 * Server Error") say so plainly and name the two things worth checking, rather
 * than dressing up a status code as an explanation.
 */
export function mcpErrorMessage(err, fallback) {
    const data = err?.response?.data;
    const detail = data?.detail;

    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length) {
        return detail
            .map((d) => d?.msg || d?.message || (typeof d === 'string' ? d : safeStringify(d)))
            .join('; ');
    }
    if (detail && typeof detail === 'object') {
        return detail.message || detail.msg || safeStringify(detail);
    }

    const named = data?.message || data?.error
        || (typeof data === 'string' && data.trim() && !data.trimStart().startsWith('<')
            ? data.trim()
            : null);

    // "Internal Server Error" is the body FastAPI returns when nothing caught
    // the exception -- it names a status, not a cause, so it is not an answer.
    if (named && named.toLowerCase() !== 'internal server error') return named;

    const status = err?.response?.status;
    if (status >= 500) {
        return `The MCP server could not be reached and no reason was returned (HTTP ${status}). `
            + 'Check the Server URL resolves, and that the credential is still valid.';
    }
    return named || err?.message || fallback;
}

function safeStringify(v) {
    if (v == null) return '(empty)';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
